import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
} from 'remotion';

const BG = '#0B1120';
const PRIMARY = '#00C9A7';
const SECONDARY = '#FF8A5C';
const ACCENT = '#845EC2';

const phaseOpacity = (frame: number, start: number, end: number, fade = 18) => {
  const fadeIn = interpolate(frame, [start, start + fade], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });
  const fadeOut = interpolate(frame, [end - fade, end], [1, 0], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });
  return fadeIn * fadeOut;
};

const DNAHelix: React.FC<{ frame: number; width: number; height: number }> = ({
  frame,
  width,
  height,
}) => {
  const turns = 2.4;
  const amp = 42;
  const cx = width / 2;
  const steps = 80;
  const path1: string[] = [];
  const path2: string[] = [];
  for (let i = 0; i <= steps; i++) {
    const t = (i / steps) * turns * Math.PI * 2;
    const y = (i / steps) * (height - 40) + 20;
    const x1 = cx + Math.sin(t + frame * 0.04) * amp;
    const x2 = cx + Math.sin(t + Math.PI + frame * 0.04) * amp;
    path1.push(`${i === 0 ? 'M' : 'L'} ${x1} ${y}`);
    path2.push(`${i === 0 ? 'M' : 'L'} ${x2} ${y}`);
  }
  const rungs: React.ReactNode[] = [];
  for (let i = 0; i < 12; i++) {
    const p = (i + 0.5) / 12;
    const t = p * turns * Math.PI * 2;
    const y = p * (height - 40) + 20;
    const x1 = cx + Math.sin(t + frame * 0.04) * amp;
    const x2 = cx + Math.sin(t + Math.PI + frame * 0.04) * amp;
    const op = interpolate(frame, [30 + i * 4, 50 + i * 4], [0, 1], {
      extrapolateLeft: 'clamp',
      extrapolateRight: 'clamp',
    });
    rungs.push(
      <line
        key={i}
        x1={x1}
        y1={y}
        x2={x2}
        y2={y}
        stroke={i % 2 === 0 ? PRIMARY : SECONDARY}
        strokeWidth={3}
        opacity={op * 0.85}
      />,
    );
  }
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`}>
      <defs>
        <linearGradient id="strand1" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor={PRIMARY} />
          <stop offset="100%" stopColor={ACCENT} />
        </linearGradient>
        <linearGradient id="strand2" x1="100%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor={SECONDARY} />
          <stop offset="100%" stopColor={PRIMARY} />
        </linearGradient>
        <filter id="helixGlow">
          <feGaussianBlur stdDeviation="2" result="b" />
          <feMerge>
            <feMergeNode in="b" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>
      <path
        d={path1.join(' ')}
        fill="none"
        stroke="url(#strand1)"
        strokeWidth={6}
        strokeLinecap="round"
        filter="url(#helixGlow)"
      />
      <path
        d={path2.join(' ')}
        fill="none"
        stroke="url(#strand2)"
        strokeWidth={6}
        strokeLinecap="round"
        filter="url(#helixGlow)"
      />
      {rungs}
    </svg>
  );
};

const AllelePill: React.FC<{
  text: string;
  dominant: boolean;
  x: number;
  y: number;
  show: number;
  label?: string;
}> = ({ text, dominant, x, y, show, label }) => {
  const gid = `pill-${dominant ? 'd' : 'r'}`;
  return (
    <g opacity={show}>
      <defs>
        <linearGradient id={gid} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor={dominant ? PRIMARY : ACCENT} stopOpacity={0.95} />
          <stop offset="100%" stopColor={dominant ? '#009E84' : '#6B3FA0'} stopOpacity={0.85} />
        </linearGradient>
        <filter id={`${gid}-glow`}>
          <feGaussianBlur stdDeviation="5" result="b" />
          <feMerge>
            <feMergeNode in="b" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>
      <rect
        x={x - 38}
        y={y - 26}
        width={76}
        height={52}
        rx={26}
        fill={`url(#${gid})`}
        stroke={dominant ? PRIMARY : ACCENT}
        strokeWidth={2.5}
        filter={`url(#${gid}-glow)`}
      />
      <text
        x={x}
        y={y + 9}
        textAnchor="middle"
        fill="#fff"
        fontSize={28}
        fontWeight={800}
        fontFamily="system-ui, sans-serif"
      >
        {text}
      </text>
      {label && (
        <text
          x={x}
          y={y + 52}
          textAnchor="middle"
          fill={dominant ? `${PRIMARY}bb` : `${ACCENT}bb`}
          fontSize={22}
          fontWeight={600}
        >
          {label}
        </text>
      )}
    </g>
  );
};

const PunnettSquare: React.FC<{ frame: number; startFrame: number }> = ({ frame, startFrame }) => {
  const t = Math.max(0, frame - startFrame);
  const cellSize = 180;
  const svgW = 900;
  const svgH = 560;
  const gridW = cellSize * 2;
  const baseX = (svgW - gridW) / 2 + 60;
  const baseY = 140;
  const grid = [
    ['AA', 'Aa'],
    ['Aa', 'aa'],
  ];
  const labels = [
    { x: baseX - 80, y: baseY + cellSize / 2 + 8, t: 'A', sub: '显性' },
    { x: baseX - 80, y: baseY + cellSize * 1.5 + 8, t: 'a', sub: '隐性' },
    { x: baseX + cellSize / 2, y: baseY - 50, t: 'A', sub: '显性' },
    { x: baseX + cellSize * 1.5, y: baseY - 50, t: 'a', sub: '隐性' },
  ];
  const lineOp = interpolate(t, [0, 25], [0, 1], { extrapolateRight: 'clamp' });
  const cells: React.ReactNode[] = [];
  for (let r = 0; r < 2; r++) {
    for (let c = 0; c < 2; c++) {
      const idx = r * 2 + c;
      const cellOp = interpolate(t, [20 + idx * 18, 45 + idx * 18], [0, 1], {
        extrapolateLeft: 'clamp',
        extrapolateRight: 'clamp',
      });
      const gx = baseX + c * cellSize;
      const gy = baseY + r * cellSize;
      const geno = grid[r][c];
      const dom = geno !== 'aa';
      cells.push(
        <g key={`${r}-${c}`} opacity={cellOp}>
          <rect
            x={gx + 6}
            y={gy + 6}
            width={cellSize - 12}
            height={cellSize - 12}
            rx={18}
            fill={dom ? 'rgba(0,201,167,0.15)' : 'rgba(132,94,194,0.2)'}
            stroke={dom ? PRIMARY : ACCENT}
            strokeWidth={2.5}
          />
          <text
            x={gx + cellSize / 2}
            y={gy + cellSize / 2 + 14}
            textAnchor="middle"
            fill="#fff"
            fontSize={44}
            fontWeight={800}
            fontFamily="monospace"
          >
            {geno}
          </text>
        </g>,
      );
    }
  }
  return (
    <svg width={svgW} height={svgH} viewBox={`0 0 ${svgW} ${svgH}`}>
      <defs>
        <filter id="punnett-glow">
          <feGaussianBlur stdDeviation="3" result="b" />
          <feMerge>
            <feMergeNode in="b" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>
      <text
        x={baseX + cellSize} y={38}
        textAnchor="middle" fill="rgba(255,255,255,0.45)" fontSize={22} fontFamily="sans-serif"
      >
        父本配子 →
      </text>
      <text
        x={baseX - 130} y={baseY + cellSize}
        textAnchor="middle" fill="rgba(255,255,255,0.45)" fontSize={22} fontFamily="sans-serif"
        transform={`rotate(-90, ${baseX - 130}, ${baseY + cellSize})`}
      >
        母本配子 ↓
      </text>
      {labels.map((L, i) => {
        const op = interpolate(t, [8 + i * 6, 28 + i * 6], [0, 1], {
          extrapolateLeft: 'clamp',
          extrapolateRight: 'clamp',
        });
        return (
          <g key={i} opacity={op}>
            <text
              x={L.x}
              y={L.y}
              textAnchor="middle"
              fill={SECONDARY}
              fontSize={36}
              fontWeight={800}
              filter="url(#punnett-glow)"
            >
              {L.t}
            </text>
          </g>
        );
      })}
      <g opacity={lineOp}>
        <line x1={baseX} y1={baseY} x2={baseX} y2={baseY + cellSize * 2}
          stroke="rgba(255,255,255,0.3)" strokeWidth={2} />
        <line x1={baseX + cellSize} y1={baseY} x2={baseX + cellSize} y2={baseY + cellSize * 2}
          stroke="rgba(255,255,255,0.3)" strokeWidth={2} />
        <line x1={baseX} y1={baseY} x2={baseX + cellSize * 2} y2={baseY}
          stroke="rgba(255,255,255,0.3)" strokeWidth={2} />
        <line x1={baseX} y1={baseY + cellSize} x2={baseX + cellSize * 2} y2={baseY + cellSize}
          stroke="rgba(255,255,255,0.3)" strokeWidth={2} />
      </g>
      {cells}
    </svg>
  );
};

const GenotypeBars: React.FC<{ frame: number; startFrame: number }> = ({ frame, startFrame }) => {
  const t = Math.max(0, frame - startFrame);
  const data = [
    { label: 'AA', ratio: 1, max: 4, pct: '25%', color: PRIMARY },
    { label: 'Aa', ratio: 2, max: 4, pct: '50%', color: SECONDARY },
    { label: 'aa', ratio: 1, max: 4, pct: '25%', color: ACCENT },
  ];
  const svgW = 900;
  const svgH = 460;
  const barW = 120;
  const gap = 100;
  const maxH = 300;
  const totalBarsW = data.length * barW + (data.length - 1) * gap;
  const baseX = (svgW - totalBarsW) / 2;
  const baseY = 400;
  return (
    <svg width={svgW} height={svgH} viewBox={`0 0 ${svgW} ${svgH}`}>
      <defs>
        {data.map((d) => (
          <linearGradient key={d.label} id={`bar-${d.label}`} x1="0%" y1="100%" x2="0%" y2="0%">
            <stop offset="0%" stopColor={d.color} stopOpacity={0.7} />
            <stop offset="100%" stopColor={d.color} stopOpacity={1} />
          </linearGradient>
        ))}
        <filter id="bar-glow">
          <feGaussianBlur stdDeviation="4" result="b" />
          <feMerge>
            <feMergeNode in="b" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>
      <text x={svgW / 2} y={48} textAnchor="middle" fill="#fff" fontSize={40} fontWeight={800}>
        基因型比例 1 : 2 : 1
      </text>
      {data.map((d, i) => {
        const delay = i * 22;
        const hProg = interpolate(t, [delay, delay + 50], [0, 1], {
          extrapolateLeft: 'clamp',
          extrapolateRight: 'clamp',
        });
        const h = (d.ratio / d.max) * maxH * hProg;
        const x = baseX + i * (barW + gap);
        return (
          <g key={d.label}>
            <rect
              x={x} y={baseY - h} width={barW} height={h} rx={14}
              fill={`url(#bar-${d.label})`}
              filter="url(#bar-glow)"
            />
            <text
              x={x + barW / 2} y={baseY + 36} textAnchor="middle"
              fill="rgba(255,255,255,0.8)" fontSize={28} fontWeight={700}
            >
              {d.label}
            </text>
            <text
              x={x + barW / 2} y={baseY - h - 18} textAnchor="middle"
              fill="#fff" fontSize={26} fontWeight={800} opacity={hProg}
            >
              {d.pct}
            </text>
          </g>
        );
      })}
      <line x1={baseX - 20} y1={baseY} x2={baseX + totalBarsW + 20} y2={baseY}
        stroke="rgba(255,255,255,0.2)" strokeWidth={2} />
    </svg>
  );
};

const PhenotypeViz: React.FC<{ frame: number }> = ({ frame }) => {
  const svgW = 900;
  const svgH = 500;
  const barW = 180;
  const gap = 120;
  const baseY = 420;
  const totalW = barW * 2 + gap;
  const startX = (svgW - totalW) / 2;
  const domH = interpolate(frame % 100, [0, 60], [0, 240], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });
  const recH = interpolate(frame % 100, [20, 80], [0, 120], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });
  return (
    <svg width={svgW} height={svgH} viewBox={`0 0 ${svgW} ${svgH}`}>
      <defs>
        <linearGradient id="pheno-dom" x1="0%" y1="100%" x2="0%" y2="0%">
          <stop offset="0%" stopColor={PRIMARY} stopOpacity={0.7} />
          <stop offset="100%" stopColor={PRIMARY} stopOpacity={1} />
        </linearGradient>
        <linearGradient id="pheno-rec" x1="0%" y1="100%" x2="0%" y2="0%">
          <stop offset="0%" stopColor={ACCENT} stopOpacity={0.7} />
          <stop offset="100%" stopColor={ACCENT} stopOpacity={1} />
        </linearGradient>
        <filter id="pheno-glow">
          <feGaussianBlur stdDeviation="4" result="b" />
          <feMerge>
            <feMergeNode in="b" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>
      <text x={svgW / 2} y={48} textAnchor="middle" fill="#fff" fontSize={40} fontWeight={800}>
        表型比 3 : 1（完全显性）
      </text>
      <rect x={startX} y={baseY - domH} width={barW} height={domH} rx={16}
        fill="url(#pheno-dom)" filter="url(#pheno-glow)" />
      <text x={startX + barW / 2} y={baseY + 36} textAnchor="middle"
        fill="rgba(255,255,255,0.7)" fontSize={24} fontWeight={600}>
        显性（AA+Aa）
      </text>
      <text x={startX + barW / 2} y={baseY - domH - 18} textAnchor="middle"
        fill="#fff" fontSize={30} fontWeight={800}>
        75%
      </text>
      <rect x={startX + barW + gap} y={baseY - recH} width={barW} height={recH} rx={16}
        fill="url(#pheno-rec)" filter="url(#pheno-glow)" />
      <text x={startX + barW + gap + barW / 2} y={baseY + 36} textAnchor="middle"
        fill="rgba(255,255,255,0.7)" fontSize={24} fontWeight={600}>
        隐性（aa）
      </text>
      <text x={startX + barW + gap + barW / 2} y={baseY - recH - 18} textAnchor="middle"
        fill="#fff" fontSize={30} fontWeight={800}>
        25%
      </text>
      <line x1={startX - 30} y1={baseY} x2={startX + totalW + 30} y2={baseY}
        stroke="rgba(255,255,255,0.2)" strokeWidth={2} />
    </svg>
  );
};

const ParentChildDiagram: React.FC<{ show: number }> = ({ show }) => (
  <svg width={560} height={200} viewBox="0 0 560 200" style={{ opacity: show }}>
    <defs>
      <linearGradient id="pcd-father" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor={PRIMARY} stopOpacity={0.35} />
        <stop offset="100%" stopColor={PRIMARY} stopOpacity={0.1} />
      </linearGradient>
      <linearGradient id="pcd-mother" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor={SECONDARY} stopOpacity={0.35} />
        <stop offset="100%" stopColor={SECONDARY} stopOpacity={0.1} />
      </linearGradient>
      <linearGradient id="pcd-child" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor={ACCENT} stopOpacity={0.28} />
        <stop offset="100%" stopColor={ACCENT} stopOpacity={0.06} />
      </linearGradient>
      <linearGradient id="pcd-arrow" x1="0%" y1="0%" x2="100%" y2="0%">
        <stop offset="0%" stopColor={PRIMARY} stopOpacity={0.5} />
        <stop offset="100%" stopColor={ACCENT} stopOpacity={0.9} />
      </linearGradient>
      <filter id="pcd-glow">
        <feGaussianBlur stdDeviation="4" result="b" />
        <feMerge>
          <feMergeNode in="b" />
          <feMergeNode in="SourceGraphic" />
        </feMerge>
      </filter>
    </defs>

    <rect
      x={16} y={24} width={140} height={64} rx={14}
      fill="url(#pcd-father)" stroke={PRIMARY} strokeWidth={2}
      filter="url(#pcd-glow)"
    />
    <circle cx={42} cy={56} r={12} fill={`${PRIMARY}30`} stroke={PRIMARY} strokeWidth={1.5} />
    <text x={42} y={61} textAnchor="middle" fill={PRIMARY} fontSize={13} fontWeight={800}>♂</text>
    <text x={100} y={62} textAnchor="middle" fill="#fff" fontSize={26} fontWeight={700}>
      父 Aa
    </text>

    <rect
      x={16} y={108} width={140} height={64} rx={14}
      fill="url(#pcd-mother)" stroke={SECONDARY} strokeWidth={2}
      filter="url(#pcd-glow)"
    />
    <circle cx={42} cy={140} r={12} fill={`${SECONDARY}30`} stroke={SECONDARY} strokeWidth={1.5} />
    <text x={42} y={145} textAnchor="middle" fill={SECONDARY} fontSize={13} fontWeight={800}>♀</text>
    <text x={100} y={146} textAnchor="middle" fill="#fff" fontSize={26} fontWeight={700}>
      母 Aa
    </text>

    <path
      d="M 162 56 C 220 56, 250 80, 290 88"
      fill="none" stroke="url(#pcd-arrow)" strokeWidth={2.5} strokeLinecap="round"
    />
    <path
      d="M 162 140 C 220 140, 250 116, 290 108"
      fill="none" stroke="url(#pcd-arrow)" strokeWidth={2.5} strokeLinecap="round"
    />

    <circle cx={218} cy={98} r={18} fill="rgba(132,94,194,0.15)" stroke={`${ACCENT}55`} strokeWidth={1.5} />
    <text x={218} y={103} textAnchor="middle" fill={`${ACCENT}cc`} fontSize={22} fontWeight={800}>×</text>

    <rect
      x={300} y={40} width={240} height={120} rx={20}
      fill="url(#pcd-child)" stroke={ACCENT} strokeWidth={2}
      filter="url(#pcd-glow)"
    />
    <text x={420} y={80} textAnchor="middle" fill="#fff" fontSize={24} fontWeight={700}>
      子代基因型
    </text>
    <text x={420} y={116} textAnchor="middle" fill="rgba(255,255,255,0.65)" fontSize={22} fontWeight={500}
      letterSpacing={3}
    >
      AA · Aa · Aa · aa
    </text>
    <text x={420} y={146} textAnchor="middle" fill={`${ACCENT}88`} fontSize={22} fontWeight={600}>
      比例 1 : 2 : 1
    </text>
  </svg>
);

export const GeneticInheritanceAnimation: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const titleSpring = spring({ frame, fps, config: { damping: 14, stiffness: 100 } });
  const titleY = interpolate(titleSpring, [0, 1], [-48, 0]);

  const pTitle = phaseOpacity(frame, 0, 215, 22);
  const pPunnett = phaseOpacity(frame, 195, 360, 22);
  const pGeno = phaseOpacity(frame, 340, 470, 22);
  const pPheno = phaseOpacity(frame, 450, 540, 20);

  const dnaIntro = interpolate(frame, [95, 140], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });
  const parentShow = interpolate(frame, [115, 165], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });

  return (
    <AbsoluteFill
      style={{
        background: `radial-gradient(ellipse 110% 80% at 50% 0%, ${ACCENT}20 0%, transparent 45%),
          radial-gradient(ellipse 90% 70% at 0% 100%, ${PRIMARY}18 0%, transparent 50%),
          linear-gradient(180deg, #08090c 0%, ${BG} 45%, #0F1419 100%)`,
        fontFamily: 'system-ui, -apple-system, PingFang SC, sans-serif',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          position: 'absolute',
          inset: 0,
          backgroundImage: `linear-gradient(135deg, transparent 40%, rgba(132,94,194,0.06) 50%, transparent 60%)`,
          backgroundSize: '80px 80px',
          pointerEvents: 'none',
        }}
      />

      <div
        style={{
          position: 'absolute',
          top: 46,
          left: 72,
          right: 64,
          opacity: pTitle * titleSpring,
          transform: `translateY(${titleY}px)`,
        }}
      >
        <h1
          style={{
            margin: 0,
            fontSize: 76,
            fontWeight: 900,
            background: `linear-gradient(105deg, ${PRIMARY}, ${SECONDARY}, ${ACCENT})`,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
          }}
        >
          孟德尔遗传定律
        </h1>
        <p style={{ margin: '14px 0 0', fontSize: 28, color: 'rgba(255,255,255,0.48)' }}>
          分离定律 · 一对等位基因的传递
        </p>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginTop: 50,
            marginLeft: 200,
            opacity: dnaIntro,
          }}
        >
          <div style={{ flex: 1, maxWidth: 520 }}>
            <h2 style={{ margin: '0 0 12px', fontSize: 40, color: PRIMARY, fontWeight: 800 }}>
              DNA 双螺旋与等位基因
            </h2>
            <p style={{ fontSize: 24, lineHeight: 1.65, color: 'rgba(255,255,255,0.52)' }}>
              基因位于染色体上；同源染色体相同位点上的不同形式称为等位基因（如 A 与 a）。配子形成时等位基因彼此分离。
            </p>
            <svg width={340} height={90} style={{ marginTop: 20 }}>
              <AllelePill text="A" dominant x={60} y={32} show={dnaIntro} label="显性" />
              <text
                x={145} y={40} textAnchor="middle"
                fill="rgba(255,255,255,0.3)" fontSize={22} fontWeight={700}
                opacity={dnaIntro}
              >
                vs
              </text>
              <AllelePill text="a" dominant={false} x={230} y={32} show={dnaIntro} label="隐性" />
            </svg>
            <div style={{ marginTop: 24 }}>
              <ParentChildDiagram show={parentShow} />
            </div>
          </div>
          <div style={{ marginLeft: -100, flexShrink: 0 }}>
            <DNAHelix frame={frame} width={500} height={560} />
          </div>
        </div>
      </div>

      <div
        style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          opacity: pPunnett,
          pointerEvents: 'none',
        }}
      >
        <h2 style={{ margin: '0 0 12px', fontSize: 40, color: SECONDARY, fontWeight: 800, textAlign: 'center' }}>
          庞尼特方格：Aa × Aa
        </h2>
        <p style={{ fontSize: 28, color: 'rgba(255,255,255,0.5)', marginBottom: 12, maxWidth: 1200, textAlign: 'center' }}>
          每个亲本产生含 A 或 a 的配子，随机结合得到四种基因型组合，概率各由格子面积表示。
        </p>
        <PunnettSquare frame={frame} startFrame={200} />
      </div>

      <div
        style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          opacity: pGeno,
          pointerEvents: 'none',
        }}
      >
        <GenotypeBars frame={frame} startFrame={345} />
        <p
          style={{
            marginTop: 12,
            fontSize: 28,
            color: 'rgba(255,255,255,0.5)',
            maxWidth: 1000,
            textAlign: 'center',
          }}
        >
          统计大量子代：AA 占 1/4，Aa 占 1/2，aa 占 1/4，符合 (1:2:1) 分离比。
        </p>
      </div>

      <div
        style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          opacity: pPheno,
          pointerEvents: 'none',
        }}
      >
        <PhenotypeViz frame={frame} />
        <p style={{ marginTop: 16, fontSize: 28, color: 'rgba(255,255,255,0.5)', maxWidth: 1100, textAlign: 'center' }}>
          当 A 对 a 完全显性时，AA 与 Aa 表型相同，故表型比为 3（显性）: 1（隐性）。
        </p>
      </div>

      <div
        style={{
          position: 'absolute',
          bottom: 48,
          left: 0,
          right: 0,
          textAlign: 'center',
          opacity: interpolate(frame, [500, 535], [0, 1], {
            extrapolateLeft: 'clamp',
            extrapolateRight: 'clamp',
          }),
        }}
      >
        <span
          style={{
            display: 'inline-block',
            padding: '14px 36px',
            borderRadius: 999,
            background: 'rgba(15,23,42,0.75)',
            border: `1px solid ${PRIMARY}55`,
            color: 'rgba(255,255,255,0.75)',
            fontSize: 22,
            fontWeight: 600,
          }}
        >
          分离定律：配子中只含一对等位基因中的一个
        </span>
      </div>

      <div
        style={{
          position: 'absolute',
          bottom: 16,
          right: 32,
          fontSize: 24,
          color: 'rgba(255,255,255,0.22)',
        }}
      >
        遗传学动画 · 示意教学
      </div>
    </AbsoluteFill>
  );
};
